import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Positions } from '../types/EmployeeTypes';
import {
    getPositions,
    addPosition,
    updatePosition,
    deletePosition
} from '../api/parametersApi';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';

// Encabezado usado tanto al exportar como al importar el archivo Excel de cargos
const CARGO_HEADER = 'Descripción del Cargo';

const PositionManager: React.FC = () => {
    const [positions, setPositions] = useState<Positions[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    // Con listas largas de cargos hace falta buscar; la tabla tiene una sola columna,
    // así que un filtro de texto basta (no un desplegable por columna).
    const [filter, setFilter] = useState('');

    // Estado para el mensaje de éxito
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Estado para el formulario de CREAR
    const [formData, setFormData] = useState<Partial<Positions>>({ group: 0, description: '' });

    // Estados para el Modal de EDICIÓN
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editData, setEditData] = useState<Partial<Positions>>({ group: 0, description: '' });
    const [editingId, setEditingId] = useState<number | null>(null);

    useEffect(() => {
        fetchPositions();
    }, []);

    const fetchPositions = async () => {
        try {
            setLoading(true);
            const data = await getPositions();
            // Orden alfabético por descripción (defensivo; el backend ya ordena)
            const sorted = [...data].sort((a, b) =>
                (a.description || '').localeCompare(b.description || '', 'es', { sensitivity: 'base' })
            );
            setPositions(sorted);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Error al cargar los cargos');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await addPosition(formData);
            setFormData({ group: 0, description: '' }); // Limpiar input
            fetchPositions();
            setSuccessMessage("Cargo agregado correctamente"); // <--- Mensaje de éxito
        } catch (err: any) {
            const backendMessage = err.response?.data?.mensaje || err.message;
            alert('Error al agregar el cargo: ' + backendMessage);
        }
    };

    const handleEditClick = (position: Positions) => {
        setEditingId(position.id as number);
        setEditData({ group: position.group, description: position.description });
        setIsEditDialogOpen(true);
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditData({ ...editData, [e.target.name]: e.target.value });
    };

    const handleUpdateSubmit = async () => {
        if (!editingId) return;
        try {
            await updatePosition(editingId, editData);
            setIsEditDialogOpen(false);
            fetchPositions();
            setSuccessMessage("Cargo actualizado correctamente"); // Opcional: También avisa al editar
        } catch (err: any) {
            const backendMessage = err.response?.data?.mensaje || err.message;
            alert('Error al actualizar el cargo: ' + backendMessage);
        }
    };

    const handleCloseDialog = () => {
        setIsEditDialogOpen(false);
        setEditingId(null);
    };

    // --- LÓGICA DE ELIMINACIÓN ---
    const handleDelete = async (id: number) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este cargo?')) {
            try {
                await deletePosition(id);
                fetchPositions();
                setSuccessMessage("Cargo eliminado correctamente"); // Opcional: También avisa al eliminar
            } catch (err: any) {
                alert('Error al eliminar el cargo: ' + err.message);
            }
        }
    };

    // Cerrar el mensaje de éxito
    const handleCloseSnackbar = () => {
        setSuccessMessage(null);
    };

    // --- EXPORTAR CARGOS A EXCEL ---
    const handleExport = () => {
        try {
            const rows = positions.map((p) => ({ [CARGO_HEADER]: p.description || '' }));
            const worksheet = XLSX.utils.json_to_sheet(rows, { header: [CARGO_HEADER] });
            worksheet['!cols'] = [{ wch: 40 }];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Cargos');
            XLSX.writeFile(workbook, 'Cargos.xlsx');
        } catch (err: any) {
            alert('Error al exportar los cargos: ' + (err?.message || err));
        }
    };

    // --- IMPORTAR CARGOS DESDE EXCEL ---
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importing, setImporting] = useState(false);

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Permite volver a seleccionar el mismo archivo si se reintenta
        e.target.value = '';
        if (!file) return;

        setImporting(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });

            // Extrae la descripción de la columna con el encabezado esperado (o la primera columna como respaldo)
            const descriptions = rows
                .map((row) => {
                    const value = CARGO_HEADER in row ? row[CARGO_HEADER] : Object.values(row)[0];
                    return String(value ?? '').trim();
                })
                .filter((desc) => desc.length > 0);

            if (descriptions.length === 0) {
                alert('El archivo no contiene cargos válidos. Verifica que exista la columna "' + CARGO_HEADER + '".');
                return;
            }

            // Evita reenviar cargos que ya existen (comparación sin distinguir mayúsculas/acentos-espacios)
            const existing = new Set(
                positions.map((p) => (p.description || '').trim().toLowerCase())
            );

            let imported = 0;
            let skipped = 0;
            const errors: string[] = [];

            for (const description of descriptions) {
                if (existing.has(description.toLowerCase())) {
                    skipped++;
                    continue;
                }
                try {
                    await addPosition({ group: 0, description });
                    existing.add(description.toLowerCase());
                    imported++;
                } catch (err: any) {
                    const backendMessage = err.response?.data?.mensaje || err.message;
                    errors.push(`"${description}": ${backendMessage}`);
                }
            }

            await fetchPositions();

            let resumen = `Importación finalizada: ${imported} agregado(s)`;
            if (skipped > 0) resumen += `, ${skipped} omitido(s) por estar duplicado(s)`;
            if (errors.length > 0) resumen += `, ${errors.length} con error(es)`;
            setSuccessMessage(resumen);

            if (errors.length > 0) {
                alert('Algunos cargos no se pudieron importar:\n' + errors.join('\n'));
            }
        } catch (err: any) {
            alert('Error al importar el archivo: ' + (err?.message || err));
        } finally {
            setImporting(false);
        }
    };

    const search = filter.trim().toLowerCase();
    const filteredPositions = search
        ? positions.filter((position) => (position.description || '').toLowerCase().includes(search))
        : positions;

    if (loading) return <Typography sx={{ p: 3 }}>Cargando información de cargos...</Typography>;
    if (error) return <Typography color="error" sx={{ p: 3 }}>{error}</Typography>;

    return (
        <Box sx={{ width: '100%', mt: 2 }}>

            {/* Formulario exclusivo para CREAR */}
            <Box
                component="form"
                onSubmit={handleCreateSubmit}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 4,
                    p: 3,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    bgcolor: '#fafafa'
                }}
            >
                <TextField
                    label="Descripción del Cargo"
                    variant="outlined"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleCreateChange}
                    placeholder="Ej: Capataz, Topógrafo"
                    required
                    fullWidth
                    size="small"
                    sx={{ bgcolor: 'white' }}
                />
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                >
                    AGREGAR CARGO
                </Button>
            </Box>

            {/* Acciones de Exportar / Importar */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx, .xls"
                    style={{ display: 'none' }}
                    onChange={handleImportFile}
                />
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FileUploadIcon />}
                    onClick={handleImportClick}
                    disabled={importing}
                >
                    {importing ? 'Importando...' : 'Importar'}
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                    disabled={positions.length === 0}
                >
                    Exportar
                </Button>
            </Box>

            {/* Buscador de cargos */}
            <TextField
                label="Buscar cargo"
                variant="outlined"
                size="small"
                fullWidth
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ mb: 1 }}
            />
            <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                {filteredPositions.length === positions.length
                    ? `${positions.length} cargos`
                    : `${filteredPositions.length} de ${positions.length} cargos`}
            </Typography>

            {/* Tabla de Registros */}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', color: '#0056b3' }}>Descripción del Cargo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#0056b3', width: '120px', textAlign: 'center' }}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPositions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    {positions.length === 0
                                        ? 'No hay cargos registrados actualmente.'
                                        : 'Ningún cargo coincide con la búsqueda.'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredPositions.map((position) => (
                                <TableRow key={position.id} hover>
                                    <TableCell>{position.description}</TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleEditClick(position)}
                                            title="Editar"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleDelete(position.id!)}
                                            title="Eliminar"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* --- MODAL DE EDICIÓN --- */}
            <Dialog open={isEditDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ color: '#0056b3', fontWeight: 'bold' }}>
                    Editar Cargo
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Descripción del Cargo"
                        name="description"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editData.description || ''}
                        onChange={handleEditChange}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleUpdateSubmit} variant="contained" color="primary">
                        Guardar Cambios
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!successMessage}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
                    {successMessage}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default PositionManager;